import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Shield,
  ShieldCheck,
  Filter,
  Activity,
  Eye,
  Gauge,
  Globe2,
  Route as RouteIcon,
  Network,
  Layers,
  Headphones,
  AlertTriangle,
  BadgeCheck,
  SignalHigh,
  Lock,
  ServerCog,
  Ban,
  ScanSearch,
  ClipboardList,
  CheckCircle,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";

const howItWorks = [
  {
    title: "1. Traffic enters the network",
    description: "A2P messages arrive from aggregators, platforms, enterprises and interconnect partners, each with its own sending profile.",
  },
  {
    title: "2. Messages are inspected",
    description: "Sender, content patterns, volumes, destinations and routing paths are examined against the rules configured for that traffic.",
  },
  {
    title: "3. Rules are applied",
    description: "Traffic is allowed, held, rate-limited or blocked according to policy, commercial agreements and local requirements.",
  },
  {
    title: "4. Activity is recorded",
    description: "Decisions and delivery outcomes are logged, so patterns can be reviewed rather than guessed at afterwards.",
  },
  {
    title: "5. Patterns are monitored",
    description: "Unusual sending behaviour, unexpected destinations and sudden volume changes are surfaced for operational review.",
  },
  {
    title: "6. Policy is adjusted",
    description: "Rules and routing are refined as sending behaviour, destination requirements and traffic mixes change over time.",
  },
];

const protectionAreas = [
  {
    title: "Unwanted and Abusive Traffic",
    description:
      "Messaging patterns that do not belong on a network, including spam-like sending, repetitive bulk pushes and traffic that ignores agreed sending rules.",
    Icon: Ban,
    color: "text-rose",
  },
  {
    title: "Grey Route Traffic",
    description:
      "A2P messages pushed through paths they were never intended for, bypassing agreed commercial arrangements and undermining delivery quality.",
    Icon: RouteIcon,
    color: "text-amber",
  },
  {
    title: "Sender ID Misuse",
    description:
      "Sender identifiers that are spoofed, reused or presented in ways that do not match the registered sender or the destination market's rules.",
    Icon: BadgeCheck,
    color: "text-indigo",
  },
  {
    title: "Content-Based Filtering",
    description:
      "Rules applied to message content and structure, so traffic that breaches policy is caught before it reaches subscribers at scale.",
    Icon: Filter,
    color: "text-sky",
  },
  {
    title: "Volume and Rate Anomalies",
    description:
      "Sudden spikes, unusual destination spreads and repetitive sending patterns that are worth inspecting before they affect the wider platform.",
    Icon: Activity,
    color: "text-lime",
  },
  {
    title: "Suspicious Traffic Sources",
    description:
      "Connections whose behaviour changes from what was agreed, reviewed against the profile expected for that customer or interconnect.",
    Icon: ScanSearch,
    color: "text-violet",
  },
  {
    title: "Infrastructure Load",
    description:
      "Protecting messaging platforms and interconnects from traffic that consumes capacity without contributing legitimate business value.",
    Icon: ServerCog,
    color: "text-navy",
  },
  {
    title: "Policy Enforcement",
    description:
      "Applying the sending rules a network, enterprise or partner has agreed to, consistently rather than case by case.",
    Icon: ClipboardList,
    color: "text-pink",
  },
];

const filteringCapabilities = [
  {
    title: "Traffic Inspection",
    description:
      "Messages are examined against configured criteria such as sender, destination, content pattern and sending rate rather than passed through untouched.",
    Icon: ScanSearch,
    color: "text-indigo",
  },
  {
    title: "Rule-Based Filtering",
    description:
      "Policies decide what is allowed, throttled or blocked, and can differ by customer, route, destination network and traffic type.",
    Icon: Filter,
    color: "text-sky",
  },
  {
    title: "Sender Controls",
    description:
      "Sender identifiers are checked against what is expected and permitted, supporting registration requirements where local rules apply.",
    Icon: BadgeCheck,
    color: "text-amber",
  },
  {
    title: "Monitoring and Alerting",
    description:
      "Traffic behaviour is watched continuously so a change in pattern becomes an operational signal instead of a later surprise.",
    Icon: Activity,
    color: "text-lime",
  },
  {
    title: "Reporting and Visibility",
    description:
      "Records of what was allowed, filtered and delivered give teams something concrete to review when a dispute or anomaly comes up.",
    Icon: Eye,
    color: "text-violet",
  },
  {
    title: "Operational Review",
    description:
      "Rules are revisited as traffic evolves, because a policy set that fitted last quarter's mix rarely fits the next one unchanged.",
    Icon: Gauge,
    color: "text-rose",
  },
];

const howWeSupport = [
  {
    title: "Telecom-Native Expertise",
    description:
      "Yootelco comes from the telecommunications side of messaging, with more than 17 years of experience in voice and messaging. Traffic questions are handled as network questions.",
    Icon: Network,
    color: "text-indigo",
  },
  {
    title: "Quality Routing",
    description:
      "Traffic is routed through connections chosen for reliability rather than lowest cost, with decisions made per destination and traffic type.",
    Icon: RouteIcon,
    color: "text-amber",
  },
  {
    title: "Global Connectivity",
    description:
      "Messaging is delivered through operator and carrier connectivity across the markets our clients serve, supporting consistent reach as traffic grows.",
    Icon: Globe2,
    color: "text-sky",
  },
  {
    title: "Delivery Monitoring",
    description:
      "Traffic is monitored so performance and behaviour changes are noticed and acted on, and so your team has insight to work from.",
    Icon: Activity,
    color: "text-lime",
  },
  {
    title: "Responsive Support",
    description:
      "A team built for enterprise and carrier clients works directly with your operations and engineering people instead of a ticket queue.",
    Icon: Headphones,
    color: "text-violet",
  },
  {
    title: "Flexible Connectivity",
    description:
      "Routing and traffic arrangements are adjusted as requirements change, rather than fixed to a single configuration indefinitely.",
    Icon: Layers,
    color: "text-rose",
  },
];

const faqs = [
  {
    question: "What is an SMS firewall?",
    answer:
      "An SMS firewall is a control layer that sits in the messaging path and inspects SMS traffic before it continues to its destination. It examines attributes such as the sender, the destination, message content patterns, volumes and the route the traffic arrived on, then applies rules that allow, throttle, hold or block that traffic. Mobile operators, messaging providers and enterprises use SMS firewalls to keep unwanted and abusive traffic off their networks, to enforce agreed sending policies, and to keep visibility over what is actually flowing through their messaging infrastructure.",
  },
  {
    question: "How does an SMS firewall work?",
    answer:
      "Traffic enters the messaging network from platforms, aggregators, enterprises or interconnect partners. The firewall inspects each message against configured criteria, including sender identifier, destination, content patterns and sending rate. Rules then decide the outcome: the message is allowed through, rate-limited, held for review or blocked. Decisions and delivery outcomes are logged so patterns can be analysed later, and monitoring surfaces unusual behaviour such as sudden volume spikes or unexpected destination spreads. Policies are refined over time as traffic mixes, destination requirements and sending behaviour change.",
  },
  {
    question: "Why do businesses need SMS traffic filtering?",
    answer:
      "Because messaging networks carry traffic from many sources, and not all of it behaves the way it should. Filtering helps keep unwanted, abusive and policy-breaking traffic away from subscribers, protects messaging platforms and interconnects from load that carries no legitimate business value, and enforces the sending rules a network or enterprise has agreed to. It also creates visibility: without inspection and logging, teams have little to work from when traffic patterns change, when a sender behaves unexpectedly, or when a destination network raises a concern about traffic quality.",
  },
  {
    question: "Is an SMS firewall the same as fraud prevention?",
    answer:
      "No. They operate at different layers. An SMS firewall is messaging infrastructure: it inspects and filters SMS traffic passing through a network and enforces sending policy. Fraud prevention in the business sense is about detecting suspicious activity in accounts, payments and logins, and then communicating those decisions to customers, often through SMS alerts. A firewall can reduce abusive and unwanted messaging traffic, but it does not detect account or payment fraud, and no firewall on its own prevents every type of fraud. The two are complementary rather than interchangeable.",
  },
  {
    question: "Can an SMS firewall support A2P SMS traffic?",
    answer:
      "Yes. A2P SMS is exactly the traffic type most firewall policies are built around, because application-to-person messaging carries verification codes, transactional alerts, notifications and promotional campaigns from many different senders. Firewall rules can distinguish between traffic types, apply sender identifier checks where destination markets require registration, and treat time-sensitive messages differently from bulk campaigns. The aim is to filter what should not be there while letting legitimate A2P traffic reach subscribers with the routing and delivery quality that traffic needs.",
  },
  {
    question: "Can SMS traffic be monitored across international routes?",
    answer:
      "Yes, and international traffic is usually where monitoring matters most. Each destination brings its own mobile networks, filtering practices, sender identifier requirements and traffic conditions, so behaviour that looks normal in one market can look unusual in another. Monitoring across international routes means tracking sending patterns, delivery outcomes and route performance by destination, so a change in one market can be identified and investigated rather than averaged away in a global figure. Route choices can then be adjusted where a destination is not performing as expected.",
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "SMS Firewall",
  serviceType: "SMS firewall and A2P SMS traffic protection",
  description:
    "SMS firewall capabilities for A2P messaging traffic, covering traffic inspection and filtering, sender identifier controls, unwanted and abusive traffic management, monitoring, reporting and international traffic visibility.",
  provider: {
    "@type": "Organization",
    name: "Yootelco",
    url: "https://yootelco.com",
  },
  areaServed: "Worldwide",
  audience: {
    "@type": "Audience",
    audienceType:
      "Mobile network operators, carriers, messaging providers, CPaaS platforms, fintechs, banks and enterprises",
  },
  url: "https://yootelco.com/solutions/sms-firewall",
};

const SolutionSmsFirewall = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="SMS Firewall | A2P SMS Traffic Protection | Yootelco"
        description="Protect and manage A2P SMS traffic with an SMS firewall focused on filtering, monitoring and messaging security. Explore Yootelco's enterprise messaging capabilities."
        path="/solutions/sms-firewall"
        faqs={faqs}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              SMS Firewall Solution
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              SMS Firewall for Secure Messaging Traffic
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              An SMS firewall gives networks and enterprises control over the messaging traffic passing through their infrastructure: inspecting it, filtering what does not belong, and keeping visibility over sending behaviour. Yootelco brings a telecom-native approach to that traffic, with quality routing, monitoring and responsive operational support.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {[
                { label: "SMS Traffic Filtering", Icon: Filter },
                { label: "A2P Messaging Protection", Icon: Shield },
                { label: "Traffic Monitoring", Icon: Activity },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <item.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What is an SMS Firewall */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">What Is an SMS Firewall?</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            An SMS firewall is a control layer inside the messaging path. Rather than passing every message straight through, it inspects traffic and applies rules that decide what continues to the destination network and what does not. Mobile operators, messaging providers, CPaaS platforms and enterprises use it to keep unwanted traffic off their networks, to enforce the sending policies they have agreed to, and to keep a clear view of what is actually flowing through their messaging infrastructure.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            It is infrastructure, not a campaign tool. The firewall does not care what a message is selling; it cares whether the sender, the content pattern, the destination and the volume match what is permitted on that connection. That makes it a natural companion to{" "}
            <Link to="/solutions/a2p-sms" className="font-semibold text-primary underline-offset-4 hover:underline">
              A2P SMS
            </Link>{" "}
            traffic, where many different senders share the same routes and the same reputation.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            It is also worth stating the limits clearly. An SMS firewall manages messaging traffic. It does not detect fraud inside customer accounts, payments or logins, and no firewall on its own prevents every type of fraud. Those are separate systems with separate jobs, and the firewall's contribution is to keep the messaging layer clean and visible.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How an SMS Firewall Works</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The mechanics are straightforward, and the same pattern applies whether traffic arrives from an enterprise platform, an aggregator or an interconnect partner.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {howItWorks.map((item) => (
            <article
              key={item.title}
              className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <h3 className="text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The value is less in any single rule than in the loop: inspect, decide, record, review, adjust. Traffic mixes change constantly as senders launch campaigns, add destinations and change platforms, so a policy set that is never revisited slowly stops matching reality.
          </p>
        </div>
      </section>

      {/* Why traffic protection matters */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Why SMS Traffic Protection Matters</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Messaging networks are shared environments. Traffic from many senders travels the same routes to the same destination operators, which means the behaviour of one sender can affect how traffic from others is treated. Unwanted volume, misused sender identifiers and messages pushed down paths they were never meant for all put pressure on the same infrastructure that carries verification codes and transactional alerts.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The consequences are practical rather than theoretical. Destination networks tighten filtering when traffic quality drops. Legitimate messages get caught alongside the traffic the filters were aimed at. Time-sensitive traffic such as{" "}
            <Link to="/use-cases/otp-verification" className="font-semibold text-primary underline-offset-4 hover:underline">
              OTP verification
            </Link>{" "}
            and{" "}
            <Link to="/use-cases/transactional-alerts" className="font-semibold text-primary underline-offset-4 hover:underline">
              transactional alerts
            </Link>{" "}
            is the first to feel the difference, because those messages are only useful if they arrive quickly.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Traffic protection is therefore a delivery quality question as much as a security one. Keeping abusive and policy-breaking traffic out of the messaging path protects the reputation of the routes that carry the traffic your business depends on.
          </p>
        </div>
      </section>

      {/* Filtering and monitoring */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">SMS Traffic Filtering and Monitoring</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Filtering decides what happens to a message. Monitoring tells you whether those decisions still make sense. Both are needed: rules without visibility become stale, and visibility without rules produces reports nobody can act on.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {filteringCapabilities.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Firewall for A2P */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">SMS Firewall for A2P Messaging</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            A2P messaging is where firewall policy earns its place, because application-to-person traffic is so varied. Verification codes, payment confirmations, delivery updates, service notifications and promotional campaigns all travel as SMS, from very different senders, with very different urgency and very different acceptable volumes.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Good policy reflects that variety. Traffic types can be treated differently, sender identifiers can be checked against what is registered and permitted in a destination market, and rate controls can be applied where a sending pattern does not match the agreed profile. The goal is not to filter as much as possible. It is to filter what should not be there, while letting legitimate traffic reach subscribers with the routing and latency it needs.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            That balance matters most for enterprises whose messaging supports customer-facing workflows: platforms sending{" "}
            <Link to="/use-cases/identity-verification" className="font-semibold text-primary underline-offset-4 hover:underline">
              identity verification
            </Link>{" "}
            messages, and businesses whose alerts are read within seconds of being sent.
          </p>
        </div>
      </section>

      {/* Protecting infrastructure */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Protecting Messaging Infrastructure</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            These are the traffic behaviours firewall policy is most often built to address. Not every network sees all of them, and the mix changes over time, which is why rules are reviewed rather than set once.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {protectionAreas.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Firewall and fraud prevention */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">SMS Firewall and Fraud Prevention</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            These two topics are frequently mixed together, and separating them makes both easier to plan for. An SMS firewall works at the messaging infrastructure layer: it inspects traffic passing through a network, enforces sending policy and keeps abusive or unwanted messaging away from subscribers.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Fraud prevention in the business sense happens elsewhere. Risk engines, monitoring systems and authentication logic inside a company's own platform decide that something looks wrong, and SMS is then used to tell the customer about it. That is a communication use case, covered on our{" "}
            <Link to="/use-cases/fraud-prevention" className="font-semibold text-primary underline-offset-4 hover:underline">
              SMS fraud prevention and security alerts
            </Link>{" "}
            page, and it depends on messages arriving quickly rather than on filtering rules.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            The two reinforce each other. A messaging path kept free of abusive traffic is a path where a genuine security alert is more likely to arrive on time. But a firewall does not detect account or payment fraud, and it should not be presented as a substitute for the systems that do.
          </p>
        </div>
      </section>

      {/* Global traffic management */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Global SMS Traffic Management</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            International traffic is where policy and monitoring get genuinely difficult. Each destination brings its own mobile networks, filtering practices, sender identifier requirements and traffic conditions. Behaviour that is entirely normal in one market can be unusual in another, and a single global average hides both.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Managing traffic across markets therefore means looking at it per destination: which routes are carrying it, how sending patterns compare with what was expected, and where delivery behaviour has shifted. Route choices can then be adjusted where a destination is not performing as it should. You can read more about how we approach{" "}
            <Link to="/coverage-routes" className="font-semibold text-primary underline-offset-4 hover:underline">
              coverage and routing
            </Link>
            .
          </p>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Traffic inspected and filtered according to configured policy",
              "Sender identifier checks where destination rules require them",
              "Monitoring of sending patterns across international routes",
              "Routing configured per destination and traffic type",
              "Route changes made when a destination underperforms",
              "Reporting that teams can review and act on",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Yootelco supports */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">How Yootelco Supports SMS Traffic Management</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Yootelco works on the messaging layer: how traffic is routed, how it behaves on the way to each destination, and how quickly a problem is identified and corrected. That is the same discipline traffic protection depends on.
          </p>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {howWeSupport.map((item) => (
            <article
              key={item.title}
              className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
        <div className="mx-auto mt-[28px] max-w-[860px] text-center">
          <p className="text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Teams that care about traffic quality are usually sending high-value traffic in the first place, which is why the same approach supports{" "}
            <Link to="/industries/fintechs" className="font-semibold text-primary underline-offset-4 hover:underline">
              fintechs
            </Link>{" "}
            and{" "}
            <Link to="/industries/banks-financial-institutions" className="font-semibold text-primary underline-offset-4 hover:underline">
              banks and financial institutions
            </Link>{" "}
            alongside carriers and messaging platforms.
          </p>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Need Better Control Over Your SMS Traffic?"
        description="Tell us about your A2P traffic, filtering requirements and destination markets, and we'll show you how Yootelco can support your messaging layer."
        useCase="SMS Firewall"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Conclusion */}
      <section className="section-shell">
        <div className="mx-auto max-w-[860px] text-center">
          <h2 className="section-title">Messaging Traffic You Can See and Control</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            An SMS firewall is ultimately about control and visibility: knowing what is entering your messaging path, applying the rules you have agreed to, and being able to explain what happened when a pattern changes. Handled well, it protects both the network and the traffic that matters most to your customers.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Yootelco brings more than 17 years of telecom voice and messaging experience to that work, with quality routing, global connectivity, delivery monitoring and a support team that engages directly with your operations people. Talk to us about your traffic and destinations, or use the{" "}
            <Link to="/contact" className="font-semibold text-primary underline-offset-4 hover:underline">
              contact form
            </Link>{" "}
            to share the details.
          </p>
          <Link
            to="/contact"
            className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Contact Sales
          </Link>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
            {[
              { label: "Traffic Filtering", Icon: ShieldCheck },
              { label: "Policy Enforcement", Icon: Lock },
              { label: "Delivery Visibility", Icon: SignalHigh },
              { label: "Anomaly Review", Icon: AlertTriangle },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                <item.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default SolutionSmsFirewall;
